import {Component, inject, OnDestroy} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {IconComponent, ButtonComponent, ChipComponent, SelectModule} from '@kin/spark-ui';
import {Subscription, delay, expand, interval, retry, switchMap, takeWhile} from 'rxjs';
import {
  AdminService,
  AdminUser,
  LinkEmailResponse,
  JobStatus,
  UserDetailResponse,
  DedupEmailsResponse,
  DataSource,
  DataIngest,
  AdminUserPaymentsResponse,
  PartnerStateResetResponse,
  PartnerIneligibleResponse,
  AdminPartnerLead,
  AdminPartnerLeadLookupResponse,
  FixedWidthColumn
} from './admin.service';
import type {DuplicateEmailGroup, AccountUserSteps, CaseStep, GenerateEvidencePacket, LinkEmail, UserDetailProperty} from '@kin/property-tax-types';

type Panel = 'documents' | 'admins' | 'signatures' | 'stats' | 'data' | 'jobs' | 'dedup' | 'evidence' | 'evidenceBatch' | 'steps' | 'countyMapping';

type PublicCounty = LinkEmail['county'];

const BATCH_SALES_COLS = 10;
const BATCH_EQUITY_COLS = 10;

// County Mapping (preview)
// Lets an admin onboard a county without writing a new adapter class: pick a Kin field,
// pick one of a small fixed set of transform types, and fill in that type's params. The
// result is data (see countyMappingConfigJson), not code, saved and read by the backend's
// generic, config-driven CountyIngestAdapter. Fetch/ingest here trigger real background
// jobs against real data, this is not a sandbox.

type TransformType =
  | 'copy'
  | 'arithmetic'
  | 'fallbackChain'
  | 'constant'
  | 'lookup'
  | 'sumMatchedRows'
  | 'aggregateStat'
  | 'weightedSum'
  | 'concat'
  | 'joinMatchedRows'
  | 'matchedRowValue'
  | 'matchedRowShare';

interface TransformParamSpec {
  label: string;
  placeholder?: string;
  /** `literal` params are typed text (an operator, a separator, a list of codes), not
   *  columns: they get no column autocomplete and don't count toward "no known column". */
  kind?: 'literal';
}

interface TransformTypeOption {
  value: TransformType;
  label: string;
  description: string;
  params: TransformParamSpec[];
}

// Param order is the order the backend's generic ingest reads them in; params are saved by
// position, so never reorder an existing type's params.
const TRANSFORM_TYPE_OPTIONS: TransformTypeOption[] = [
  {
    value: 'copy',
    label: 'Direct copy',
    description: 'One column, copied as-is.',
    params: [
      {label: 'Source field', placeholder: 'e.g. parcel_id'},
    ],
  },
  {
    value: 'arithmetic',
    label: 'Arithmetic (A op B)',
    description: 'Two fields combined with +, -, *, or /.',
    params: [
      {label: 'Field A', placeholder: 'e.g. HmstdJust'},
      {label: 'Operator (+ - * /)', placeholder: '-', kind: 'literal'},
      {label: 'Field B (or a constant)', placeholder: 'e.g. HmstdAsd'},
    ],
  },
  {
    value: 'fallbackChain',
    label: 'Fallback chain',
    description: 'First non-empty field wins, in priority order.',
    params: [
      {label: 'Fields, in priority order (comma-separated)', placeholder: 'e.g. TAXES_YEAR1, TAXES_YEAR2, TAXES_YEAR3, TAXES_YEAR4'},
    ],
  },
  {
    value: 'constant',
    label: 'Constant / literal',
    description: 'A fixed value, not read from the source data at all.',
    params: [
      {label: 'Value', placeholder: 'e.g. Osceola County', kind: 'literal'},
    ],
  },
  {
    value: 'lookup',
    label: 'Legend / lookup table',
    description: 'A code column, translated via a separate legend table.',
    params: [
      {label: 'Code field', placeholder: 'e.g. ImprQual'},
      {label: 'Key column in the legend', placeholder: 'e.g. cd'},
      {label: 'Value column in the legend', placeholder: 'e.g. dscr'},
    ],
  },
  {
    value: 'concat',
    label: 'Join columns on the same row',
    description: 'Several columns on one row joined into one value (e.g. a legal description split across columns). Blank values are skipped.',
    params: [
      {label: 'Columns, in order (comma-separated)', placeholder: 'e.g. legal_1, legal_2, legal_3'},
      {label: 'Separator (optional)', placeholder: 'Defaults to ", "', kind: 'literal'},
    ],
  },
  {
    value: 'joinMatchedRows',
    label: 'Join matching rows from another table',
    description: "One column joined across all of a parcel's rows in another file (e.g. an owner name split across lines). Blank values are skipped.",
    params: [
      {label: 'Value field (in the other file)', placeholder: 'e.g. owners.csv:owner_name'},
      {label: 'Order by field (optional)', placeholder: 'e.g. line_number'},
      {label: 'Row filter (optional)', placeholder: 'e.g. owner_type=PRIMARY'},
      {label: 'Separator (optional)', placeholder: 'Defaults to ", "', kind: 'literal'},
      {label: 'Each value once (optional, type "unique")', placeholder: 'unique', kind: 'literal'},
    ],
  },
  {
    value: 'matchedRowValue',
    label: 'Pick a value from matching rows',
    description: "One value from a parcel's rows in another file whose type is in the list. A type earlier in the list wins; among those, the lowest order field, then the highest weight.",
    params: [
      {label: 'Value field (in the other file)', placeholder: 'e.g. elements.csv:description'},
      {label: 'Type field', placeholder: 'e.g. element_type'},
      {label: 'Types, in preference order (comma-separated)', placeholder: 'e.g. COND, CDU', kind: 'literal'},
      {label: 'Weight field (optional)', placeholder: 'e.g. weight'},
      {label: 'Row filter (optional)', placeholder: 'e.g. building=1'},
      {label: 'Order by field (optional)', placeholder: 'e.g. building_number'},
    ],
  },
  {
    value: 'matchedRowShare',
    label: 'Largest share of matching rows',
    description: "The highest weight among a parcel's rows in another file whose type is in the list, as a percentage of their total (e.g. the main exterior wall's share).",
    params: [
      {label: 'Weight field (in the other file)', placeholder: 'e.g. elements.csv:weight'},
      {label: 'Type field', placeholder: 'e.g. element_type'},
      {label: 'Types (comma-separated)', placeholder: 'e.g. EXTWALL', kind: 'literal'},
      {label: 'Row filter (optional)', placeholder: 'e.g. building=1'},
    ],
  },
  {
    value: 'sumMatchedRows',
    label: 'Sum matching rows from another table',
    description: 'A list of codes on this row, each looked up and summed from another table.',
    params: [
      {label: 'List field (which codes apply to this row)', placeholder: 'e.g. tax_districts'},
      {label: 'Value field to sum', placeholder: 'e.g. rate'},
      {label: 'Match key linking the two', placeholder: 'e.g. district_code'},
      {label: 'Row filter (optional)', placeholder: 'e.g. room_type=BED;building=1 or exemption=RES,HS'},
      {label: 'Multiply result by (optional)', placeholder: 'e.g. 0.01 for rates published as a percent', kind: 'literal'},
    ],
  },
  {
    value: 'weightedSum',
    label: 'Weighted sum (value × matched weight)',
    description: "Per parcel, each matching row's value times a weight looked up from another table by key, each product rounded, then summed (e.g. each district's homestead exemption × that district's tax rate).",
    params: [
      {label: 'Value field (per-parcel rows)', placeholder: 'e.g. exemptions.csv:exempt_value'},
      {label: "Key field on the value's rows", placeholder: 'e.g. exemptions.csv:district_code'},
      {label: 'Weight field (in the other table)', placeholder: 'e.g. rate'},
      {label: "Weight table's key field", placeholder: 'e.g. district_code'},
      {label: 'Row filter (optional)', placeholder: 'e.g. exemption=RES,HS'},
      {label: 'Weight filter (optional)', placeholder: 'e.g. property_type=Real;exemption=RES'},
      {label: 'Multiply result by (optional)', placeholder: 'e.g. 0.01 for rates published as a percent', kind: 'literal'},
    ],
  },
  {
    value: 'aggregateStat',
    label: 'Aggregate statistic (whole dataset)',
    description: 'Computed once across every row (e.g. a historical win rate), not per-parcel.',
    params: [
      {label: 'Group by field', placeholder: 'e.g. class_code'},
      {label: 'Compare field A', placeholder: 'e.g. initial_value'},
      {label: 'Compare field B', placeholder: 'e.g. final_value'},
      {label: "This row's group field (optional, if named differently)", placeholder: 'e.g. property_class'},
    ],
  },
];

interface KinFieldDef {
  key: string;
  label: string;
  group: string;
}

// Grouped so the editor's ~40 fields stay scannable; the table shows one header per group,
// in this order.
const KIN_FIELDS: KinFieldDef[] = [
  {key: 'parcelId', label: 'parcelId', group: 'Identity'},
  {key: 'county', label: 'county', group: 'Identity'},
  {key: 'taxYear', label: 'taxYear', group: 'Identity'},
  {key: 'ownerName', label: 'ownerName', group: 'Identity'},
  {key: 'siteStreet', label: 'siteStreet', group: 'Site address'},
  {key: 'siteCity', label: 'siteCity', group: 'Site address'},
  {key: 'siteState', label: 'siteState', group: 'Site address'},
  {key: 'siteZip', label: 'siteZip', group: 'Site address'},
  {key: 'legalDescription', label: 'legalDescription', group: 'Site address'},
  {key: 'bedrooms', label: 'bedrooms', group: 'Building'},
  {key: 'fullBaths', label: 'fullBaths', group: 'Building'},
  {key: 'halfBaths', label: 'halfBaths', group: 'Building'},
  {key: 'sqft', label: 'sqft', group: 'Building'},
  {key: 'yearBuilt', label: 'yearBuilt', group: 'Building'},
  {key: 'yearRemodeled', label: 'yearRemodeled', group: 'Building'},
  {key: 'storyHeight', label: 'storyHeight', group: 'Building'},
  {key: 'fireplaces', label: 'fireplaces', group: 'Building'},
  {key: 'effectiveArea', label: 'effectiveArea', group: 'Building'},
  {key: 'qualityCode', label: 'qualityCode', group: 'Building'},
  {key: 'qualityDescription', label: 'qualityDescription', group: 'Building'},
  {key: 'marketValueEstimate', label: 'marketValueEstimate', group: 'Values and tax'},
  {key: 'assessedValue', label: 'assessedValue', group: 'Values and tax'},
  {key: 'appraisedValue', label: 'appraisedValue', group: 'Values and tax'},
  {key: 'landValue', label: 'landValue', group: 'Values and tax'},
  {key: 'improvementValue', label: 'improvementValue', group: 'Values and tax'},
  {key: 'compositeRate', label: 'compositeRate', group: 'Values and tax'},
  {key: 'currentTaxBill', label: 'currentTaxBill', group: 'Values and tax'},
  {key: 'homesteadExemption', label: 'homesteadExemption', group: 'Values and tax'},
  {key: 'winProbability', label: 'winProbability', group: 'Values and tax'},
  {key: 'stateClass', label: 'stateClass', group: 'Classification'},
  {key: 'neighborhoodCode', label: 'neighborhoodCode', group: 'Classification'},
  {key: 'landUseCode', label: 'landUseCode', group: 'Classification'},
  {key: 'grade', label: 'grade', group: 'Evidence packet details'},
  {key: 'condition', label: 'condition', group: 'Evidence packet details'},
  {key: 'costDesign', label: 'costDesign', group: 'Evidence packet details'},
  {key: 'foundationType', label: 'foundationType', group: 'Evidence packet details'},
  {key: 'heatCool', label: 'heatCool', group: 'Evidence packet details'},
  {key: 'exteriorWall', label: 'exteriorWall', group: 'Evidence packet details'},
  {key: 'exteriorWallAdj', label: 'exteriorWallAdj', group: 'Evidence packet details'},
  {key: 'exteriorWallPct', label: 'exteriorWallPct', group: 'Evidence packet details'},
  {key: 'percentGood', label: 'percentGood', group: 'Evidence packet details'},
  {key: 'lumpSumAdj', label: 'lumpSumAdj', group: 'Evidence packet details'},
  {key: 'extraFeatureCodes', label: 'extraFeatureCodes', group: 'Evidence packet details'},
  {key: 'foundationDefect', label: 'foundationDefect', group: 'Evidence packet details'},
];

const KIN_FIELD_GROUPS: {name: string; fields: KinFieldDef[]}[] = [...new Set(KIN_FIELDS.map((f) => f.group))]
  .map((name) => ({name, fields: KIN_FIELDS.filter((f) => f.group === name)}));

interface CountyFieldMapping {
  transformType: TransformType;
  params: string[];
}

/** Display names for the "Start from" list; a jurisdiction not listed here shows its code. */
const COUNTY_MAPPING_LABELS: Record<string, string> = {
  HARRIS_TX: 'Harris',
  OSCEOLA_FL: 'Osceola',
  MANATEE_FL: 'Manatee',
};

const BLANK_COUNTY_MAPPING_LABEL = 'Blank (new county)';

/** One selectable row in the "Start from" dropdown: a jurisdiction saved on the backend,
 *  grouped by its `state`. Picking it fetches its config. */
interface CountyMappingListEntry {
  jurisdictionCode: string;
  label: string;
}

interface CountyMappingStateGroup {
  stateName: string;
  entries: CountyMappingListEntry[];
}

/** Drops trailing params left blank. A literal param keeps whitespace, since a separator can be a
 *  space; only an empty one is blank. A skipped param in the middle becomes '', since params are
 *  positional and the backend only accepts strings. */
function trimTrailingBlankParams(params: string[], specs: TransformParamSpec[] = []): string[] {
  const trimmed = Array.from(params, (p) => p ?? '');
  const isBlank = (i: number) => specs[i]?.kind === 'literal' ? !trimmed[i] : !trimmed[i]?.trim();
  while (trimmed.length > 0 && isBlank(trimmed.length - 1)) trimmed.pop();
  return trimmed;
}

/**
 * Parses a pasted header row into column names. Detects the delimiter by whichever of
 * tab, pipe, or comma appears most (matches the three real delimiters seen across
 * Harris/Osceola/Manatee/DCAD), then strips surrounding quotes DCAD's CSV headers use.
 */
const DELIMITER_TYPE: Record<string, 'tab' | 'pipe' | 'comma'> = {
  '\t': 'tab',
  '|': 'pipe',
  ',': 'comma',
};

function parseHeaderRow(raw: string): {delimiter: 'tab' | 'pipe' | 'comma'; columns: string[]} {
  const line = raw.trim();
  if (!line) return {delimiter: 'comma', columns: []};
  const counts: Record<string, number> = {
    '\t': (line.match(/\t/g) ?? []).length,
    '|': (line.match(/\|/g) ?? []).length,
    ',': (line.match(/,/g) ?? []).length,
  };
  const delimiterChar = Object.values(counts).every((n) => n === 0)
    ? ','
    : (Object.keys(counts) as string[]).reduce((a, b) => (counts[a] >= counts[b] ? a : b));
  const columns = line
    .split(delimiterChar)
    .map((c) => c.trim().replace(/^"|"$/g, ''))
    .filter((c) => c.length > 0);
  return {delimiter: DELIMITER_TYPE[delimiterChar], columns};
}

@Component({
  selector: 'app-admin',
  standalone: true,
  imports: [CommonModule, FormsModule, IconComponent, ButtonComponent, ChipComponent, SelectModule],
  templateUrl: './admin.component.html',
  styleUrl: './admin.component.css',
})
export class AdminComponent implements OnDestroy {
  private adminService = inject(AdminService);

  activePanel: Panel = 'documents';

  // Admins panel
  users: AdminUser[] = [];
  usersLoading = false;
  usersError = '';
  userSearch = '';
  adminActionLoading: string | null = null;
  adminActionError: string | null = null;
  downloadError: string | null = null;
  downloadAllError = '';

  userFilterAdmin: 'all' | 'admin' | 'non-admin' = 'all';
  userFilterStep: 'all' | 'has-step' | 'no-step' = 'all';
  userFilterSigned: 'all' | 'signed' | 'unsigned' = 'all';
  userFilterLogin: 'all' | 'has-login' | 'no-login' = 'all';

  get filteredUsers(): AdminUser[] {
    let result = this.users;
    const q = this.userSearch.trim().toLowerCase();
    if (q) {
      result = result.filter(u =>
        u.email.toLowerCase().includes(q)
        || u.phoneNumber?.toLowerCase().includes(q)
        || u.firstName?.toLowerCase().includes(q)
        || u.lastName?.toLowerCase().includes(q)
        || u.address?.toLowerCase().includes(q)
        || u.parcelId?.toLowerCase().includes(q)
        || u.currentStep?.toLowerCase().includes(q),
      );
    }
    if (this.userFilterAdmin === 'admin') {
      result = result.filter(u => u.isAdmin);
    } else if (this.userFilterAdmin === 'non-admin') {
      result = result.filter(u => !u.isAdmin);
    }
    if (this.userFilterStep === 'has-step') {
      result = result.filter(u => !!u.currentStep);
    } else if (this.userFilterStep === 'no-step') {
      result = result.filter(u => !u.currentStep);
    }
    if (this.userFilterSigned === 'signed') {
      result = result.filter(u => u.hasSigned);
    } else if (this.userFilterSigned === 'unsigned') {
      result = result.filter(u => !u.hasSigned);
    }
    if (this.userFilterLogin === 'has-login') {
      result = result.filter(u => !!u.lastLoginAt);
    } else if (this.userFilterLogin === 'no-login') {
      result = result.filter(u => !u.lastLoginAt);
    }
    return result;
  }

  downloadUsersCsv() {
    const users = this.filteredUsers;
    if (users.length === 0) return;
    const keys = Object.keys(users[0]) as (keyof AdminUser)[];
    const header = keys.join(',');
    const rows = users.map(u =>
      keys.map(k => {
        const val = u[k];
        const str = val == null ? '' : String(val);
        return str.includes(',') || str.includes('"') || str.includes('\n')
          ? `"${str.replace(/"/g, '""')}"` : str;
      }).join(','),
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], {type: 'text/csv'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'users.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  // Link email panel
  linkEmail = '';
  linkParcelId = '';
  linkCounty: PublicCounty | '' = '';
  // Shared by Link Email and Case Steps.
  readonly publicCounties: PublicCounty[] = ['harris', 'manatee', 'osceola'];
  linkTaxYear = 2025;
  linkLoading = false;
  linkSuccess: LinkEmailResponse | null = null;
  linkError = '';

  // Data panel (unified upload+ingest)
  dataSources: DataSource[] = [];
  dataSourcesLoading = false;
  dataSourcesError = '';
  dataSelectedType = '';
  dataYear = '2026';
  dataFile: File | null = null;
  dataUploadStarting = false;
  dataUploadError = '';
  dataUploadJob: JobStatus | null = null;
  private dataUploadPollSub: Subscription | null = null;
  dataIngestStarting = false;
  dataIngestError = '';
  dataIngestSuccess = '';
  dataIngestJob: JobStatus | null = null;
  private dataIngestPollSub: Subscription | null = null;
  dataAutoIngest = true;
  dataCounty = '';
  dataParcelsUploadJobId = '';
  private lastParcelsListUploadJobId = '';
  readonly dataYearOptions: string[] = (() => {
    const current = new Date().getFullYear();
    const years: string[] = [];
    for (let y = current + 1; y >= current - 3; y--) years.push(String(y));
    return years;
  })();

  // Jobs dashboard panel
  recentJobs: JobStatus[] = [];
  jobsLoading = false;
  jobsError = '';
  cancelingJob: string | null = null;
  cancelError = '';
  expandedJobId: string | null = null;
  private jobsPollSub: Subscription | null = null;
  private readonly jobsPollIntervalMs = 5000;

  // Manage User panel
  sigEmail = '';
  sigDeleting = false;
  sigSuccess = '';
  sigError = '';

  // User lookup
  lookupEmail = '';
  lookupLoading = false;
  lookupError = '';
  lookupUser: UserDetailResponse | null = null;
  addressDeleting: string | null = null;
  addressDeleteError = '';
  propertyDeleting: string | null = null;
  propertyDeleteError = '';
  lookupPayments: AdminUserPaymentsResponse | null = null;
  lookupPaymentsLoading = false;
  lookupPaymentsError = '';

  // Reset partner state
  resetEmail = '';
  resetting = false;
  resetResult: PartnerStateResetResponse | null = null;
  resetError = '';

  // Partner ineligible. Two buttons over one email rather than a state-aware toggle: this
  // control takes a typed email that need not be the one looked up above, so it cannot assume
  // it knows that email's current state before acting.
  ineligibleEmail = '';
  ineligibleBusy: 'mark' | 'unmark' | null = null;
  ineligibleResult: PartnerIneligibleResponse | null = null;
  ineligibleError = '';

  /**
   * The looked-up user's ineligible state, read from
   * `GET /admin/users/:email/partner-ineligible` on every lookup.
   *
   * `lookupIneligibleKnown` is true only when we hold a real answer from the server;
   * `lookupIneligibleAt === null` with `known` true means *not marked*, which is an answer, not
   * an absence. A failed read leaves `known` false and sets `lookupIneligibleError` — it is
   * never reported as eligible, because claiming eligible for a user we could not read is how
   * an admin marks someone twice or concludes a mark did not take.
   *
   * The mark/un-mark endpoints return the resulting state, so they write these fields directly
   * for the user currently on screen rather than triggering a second round trip. (This replaces
   * the per-email session Map that existed before the read endpoint: with a real read there is
   * only ever one user displayed, and nothing to cache across emails.)
   */
  lookupIneligibleKnown = false;
  lookupIneligibleAt: string | null = null;
  lookupIneligibleLoading = false;
  lookupIneligibleError = '';

  /**
   * Partner lead lookup. Its own email, deliberately separate from `lookupEmail`: a lead can
   * exist for an email with no `users` row at all, so this must not depend on the user search
   * having succeeded. `leadResult.found === false` is a successful answer and never sets
   * `leadError` — conflating the two is the bug this section exists to fix.
   */
  leadEmail = '';
  leadLoading = false;
  leadResult: AdminPartnerLeadLookupResponse | null = null;
  leadError = '';

  // Dedup panel
  dupGroups: DuplicateEmailGroup[] = [];
  dupLoading = false;
  dupError = '';
  dedupLoading = false;
  dedupResult: DedupEmailsResponse | null = null;
  dedupError = '';

  // Evidence packet panel
  evidenceCounty: GenerateEvidencePacket['county'] | '' = '';
  evidenceAccountNumber = '';
  evidenceSalesComps = '';
  evidenceEquityComps = '';
  evidenceLoading = false;
  evidenceError = '';
  evidenceSuccess = '';

  // Evidence Batch panel
  evidenceBatchCounty: GenerateEvidencePacket['county'] | '' = '';
  evidenceBatchYear = '2026';
  evidenceBatchCsv = '';
  evidenceBatchParseError = '';
  evidenceBatchStarting = false;
  evidenceBatchDownloading = false;
  evidenceBatchError = '';
  evidenceBatchSuccess = '';
  evidenceBatchJob: JobStatus | null = null;
  private evidenceBatchPollSub: Subscription | null = null;

  readonly batchSalesCols = BATCH_SALES_COLS;
  readonly batchEquityCols = BATCH_EQUITY_COLS;

  // Shared by the single and batch forms.
  readonly evidenceCountyOptions: GenerateEvidencePacket['county'][] = ['HARRIS_TX', 'MANATEE_FL', 'OSCEOLA_FL'];

  get evidenceBatchCsvHeader(): string {
    const sales = Array.from({ length: BATCH_SALES_COLS }, (_, i) => `sales_${i + 1}`);
    const equity = Array.from({ length: BATCH_EQUITY_COLS }, (_, i) => `equity_${i + 1}`);
    return ['subject', ...sales, ...equity].join(',');
  }

  // Case steps panel
  stepsCounty: PublicCounty | '' = '';
  stepsInput = '';
  stepsAccounts: AccountUserSteps[] = [];
  stepsLoading = false;
  stepsError = '';
  stepUpdating: string | null = null;
  stepUpdateError = '';
  stepUpdateSuccess = '';
  stepsFilterTitle = '';
  stepsFilterStatus: 'all' | 'complete' | 'active' | 'pending' = 'all';
  stepsFilterStep = '';
  bulkUpdating = false;

  get availableStepNumbers(): number[] {
    const nums = new Set<number>();
    for (const account of this.stepsAccounts) {
      for (const step of account.steps) {
        nums.add(step.step);
      }
    }
    return Array.from(nums).sort((a, b) => a - b);
  }

  get allStepsFlat(): (CaseStep & { parcelId: string; county: string; email: string })[] {
    let result: (CaseStep & { parcelId: string; county: string; email: string })[] = [];
    for (const account of this.stepsAccounts) {
      for (const step of account.steps) {
        result.push({ ...step, parcelId: account.parcelId, county: account.county, email: account.email });
      }
    }
    if (this.stepsFilterStatus !== 'all') {
      result = result.filter(s => s.status === this.stepsFilterStatus);
    }
    const stepNum = this.stepsFilterStep.trim();
    if (stepNum) {
      result = result.filter(s => s.step === Number(stepNum));
    }
    const q = this.stepsFilterTitle.trim().toLowerCase();
    if (q) {
      result = result.filter(s =>
        s.title.toLowerCase().includes(q)
        || s.parcelId.toLowerCase().includes(q)
        || s.email.toLowerCase().includes(q)
        || s.description.toLowerCase().includes(q),
      );
    }
    return result;
  }

  // Stats panel
  signedCount: number | null = null;
  signedCountLoading = false;
  signedCountError = '';

  // County Mapping panel (preview), wired to the real backend county-ingest-config endpoints
  readonly kinFields = KIN_FIELDS;
  readonly kinFieldGroups = KIN_FIELD_GROUPS;
  readonly blankCountyMappingLabel = BLANK_COUNTY_MAPPING_LABEL;
  readonly transformTypeOptions = TRANSFORM_TYPE_OPTIONS;
  // Populated from the real backend list (see `loadCountyMappingStateGroups`), not hardcoded, so
  // a county saved via "Blank (new county)" shows up here the next time this panel is opened.
  countyMappingStateGroups: CountyMappingStateGroup[] = [];
  countyMappingStateGroupsError = '';
  countyMappingTemplate = 'blank';
  countyMappingSourceFiles: string[] = [];
  // Real columns declared for each source file, parsed from a pasted header row. Lets the
  // per-field param inputs offer real autocomplete instead of a blind text box, which is
  // the whole difference between typing 3 known counties from memory and working through a
  // real, unfamiliar 14-file county like DCAD without a typo silently breaking a mapping.
  countyMappingSourceFileColumns: Record<string, string[] | FixedWidthColumn[]> = {};
  countyMappingSourceFileDelimiters: Record<string, 'tab' | 'pipe' | 'comma' | 'fixedWidth'> = {};
  countyMappingSourceFileUrls: Record<string, string> = {};
  countyMappingNewFileInput = '';
  countyMappingNewFileHeaderInput = '';
  countyMappingFields: Record<string, CountyFieldMapping> = {};
  countyMappingSelectedField: string | null = null;
  countyMappingNewFileError = '';
  countyMappingLayoutBusy = false;
  countyMappingLayoutStatus = '';
  countyMappingLayoutError = '';

  selectPanel(panel: Panel) {
    this.activePanel = panel;
    if (panel === 'admins' && this.users.length === 0) {
      this.loadUsers();
    }
    if (panel === 'stats' && this.signedCount === null) {
      this.loadSignedCount();
    }
    if (panel === 'dedup' && this.dupGroups.length === 0 && !this.dedupResult) {
      this.loadDuplicateEmails();
    }
    if (panel === 'jobs') {
      if (this.recentJobs.length === 0) {
        this.loadJobs();
      }
      this.startJobsPoll();
    } else {
      this.stopJobsPoll();
    }
    if (panel === 'data' && this.dataSources.length === 0 && !this.dataSourcesLoading) {
      this.loadDataSources();
    }
    if (panel === 'countyMapping' && Object.keys(this.countyMappingFields).length === 0) {
      this.startBlankCountyMapping();
    }
    if (panel === 'countyMapping' && this.countyMappingStateGroups.length === 0) {
      this.loadCountyMappingStateGroups();
    }
  }

  loadDataSources() {
    this.dataSourcesLoading = true;
    this.dataSourcesError = '';
    this.adminService.listDataSources().subscribe({
      next: (sources) => {
        this.dataSources = sources;
        this.dataSourcesLoading = false;
        if (!this.dataSelectedType && sources.length > 0) {
          this.dataSelectedType = sources[0].id;
        }
        this.dataCounty = '';
      },
      error: () => {
        this.dataSourcesLoading = false;
        this.dataSourcesError = 'Failed to load data sources.';
      },
    });
  }

  get selectedDataSource(): DataSource | null {
    return this.dataSources.find(s => s.id === this.dataSelectedType) ?? null;
  }

  get dataYearValidForSource(): boolean {
    const source = this.selectedDataSource;
    if (!source) return false;
    const needsYear =
      source.upload.params?.includes('year') ||
      source.ingest.params?.includes('year');
    return !needsYear || this.dataYear.trim().length > 0;
  }

  get dataCountyValidForSource(): boolean {
    const source = this.selectedDataSource;
    if (!source) return false;
    return !source.upload.params?.includes('county') || this.dataCounty.trim().length > 0;
  }

  onDataTypeChange() {
    this.dataFile = null;
    this.dataUploadJob = null;
    this.dataUploadError = '';
    this.dataIngestJob = null;
    this.dataIngestError = '';
    this.dataIngestSuccess = '';
    this.dataCounty = '';
    this.dataParcelsUploadJobId = this.selectedDataSource?.ingest.params?.includes('parcelsUploadJobId')
      ? this.lastParcelsListUploadJobId
      : '';
    this.stopDataUploadPoll();
    this.stopDataIngestPoll();
  }

  onDataFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    this.dataFile = input.files?.[0] ?? null;
    this.dataUploadError = '';
  }

  submitDataUpload() {
    const source = this.selectedDataSource;
    if (!source || !source.upload.supported || !this.dataFile) return;
    const year = source.upload.params?.includes('year') ? this.dataYear.trim() : undefined;
    if (source.upload.params?.includes('year') && !year) return;
    if (!this.dataCountyValidForSource) return;
    this.dataUploadStarting = true;
    this.dataUploadError = '';
    this.dataUploadJob = null;
    this.dataIngestJob = null;
    this.dataIngestSuccess = '';
    this.dataIngestError = '';
    this.stopDataUploadPoll();
    this.stopDataIngestPoll();
    const upload$ = source.id === 'county_data'
      ? this.adminService.uploadCountyData(this.dataFile, this.dataCounty)
      : this.adminService.uploadData(this.dataFile, source.id, year, source.upload.params?.includes('county') ? this.dataCounty : undefined);
    upload$.subscribe({
      next: (res) => {
        this.dataUploadStarting = false;
        this.dataFile = null;
        this.pollDataUploadStatus(res.jobId, source.id);
      },
      error: () => {
        this.dataUploadStarting = false;
        this.dataUploadError = 'Failed to start upload. Check the file and try again.';
      },
    });
  }

  // A single failed status check (e.g. a request landing on a task mid-rotation
  // during a deploy) must not kill polling permanently — without this, the UI would
  // freeze on a stale "Running..." state forever, even after the job actually
  // completes, since nothing would be left watching to notice.
  private static readonly JOB_POLL_RETRY = {count: 5, delay: 5000};

  private pollDataUploadStatus(jobId: string, sourceId: string) {
    this.dataUploadPollSub = this.adminService.getJobStatus(jobId).pipe(
      retry(AdminComponent.JOB_POLL_RETRY),
      expand(() => this.adminService.getJobStatus(jobId).pipe(delay(5000), retry(AdminComponent.JOB_POLL_RETRY))),
      takeWhile((job) => job.status !== 'completed' && job.status !== 'failed' && job.status !== 'cancelled', true),
    ).subscribe({
      next: (job) => {
        this.dataUploadJob = job;
        if (job.status === 'completed' && sourceId === 'parcels_list') {
          this.lastParcelsListUploadJobId = job.id;
          if (this.selectedDataSource?.ingest.params?.includes('parcelsUploadJobId') && !this.dataParcelsUploadJobId) {
            this.dataParcelsUploadJobId = job.id;
          }
        }
        if (job.status === 'completed' && this.dataAutoIngest && this.selectedDataSource?.ingest.supported) {
          this.submitDataIngest(job.id);
        } else if (job.status === 'failed') {
          this.dataUploadError = job.errorMessage || 'Upload failed.';
        } else if (job.status === 'cancelled') {
          this.dataUploadError = 'Upload was cancelled.';
        }
      },
      error: () => {
        this.dataUploadError = 'Failed to check upload status after several retries — the upload may still be running. Check the Jobs page.';
      },
    });
  }

  private stopDataUploadPoll() {
    this.dataUploadPollSub?.unsubscribe();
    this.dataUploadPollSub = null;
  }

  cancelDataUpload() {
    if (!this.dataUploadJob) return;
    const jobId = this.dataUploadJob.id;
    this.adminService.cancelJob(jobId).subscribe({
      next: (job) => {
        this.dataUploadJob = job;
        this.stopDataUploadPoll();
        this.dataUploadError = 'Upload was cancelled.';
      },
      error: () => {
        this.dataUploadError = 'Failed to cancel upload.';
      },
    });
  }

  submitDataIngest(uploadJobId?: string) {
    const source = this.selectedDataSource;
    if (!source || !source.ingest.supported) return;
    const params = source.ingest.params ?? [];
    const payload: DataIngest = { type: source.id };
    if (params.includes('year')) {
      const year = this.dataYear.trim();
      if (!year) return;
      payload.year = year;
    }
    if (params.includes('uploadJobId')) {
      if (!uploadJobId) {
        this.dataIngestError = 'This source needs to be uploaded first — use Upload & Ingest.';
        return;
      }
      payload.uploadJobId = uploadJobId;
    }
    if (params.includes('parcelsUploadJobId') && this.dataParcelsUploadJobId.trim()) {
      payload.parcelsUploadJobId = this.dataParcelsUploadJobId.trim();
    }
    this.dataIngestStarting = true;
    this.dataIngestError = '';
    this.dataIngestSuccess = '';
    this.dataIngestJob = null;
    this.stopDataIngestPoll();
    this.adminService.ingestData(payload).subscribe({
      next: (res) => {
        this.dataIngestStarting = false;
        this.pollDataIngestStatus(res.jobId);
      },
      error: () => {
        this.dataIngestStarting = false;
        this.dataIngestError = 'Failed to start ingest. Check the year and try again.';
      },
    });
  }

  private pollDataIngestStatus(jobId: string) {
    this.dataIngestPollSub = this.adminService.getJobStatus(jobId).pipe(
      retry(AdminComponent.JOB_POLL_RETRY),
      expand(() => this.adminService.getJobStatus(jobId).pipe(delay(5000), retry(AdminComponent.JOB_POLL_RETRY))),
      takeWhile((job) => job.status !== 'completed' && job.status !== 'failed' && job.status !== 'cancelled', true),
    ).subscribe({
      next: (job) => {
        this.dataIngestJob = job;
        if (job.status === 'completed') {
          // `job.progress` is a 0-100 percentage, not a row count — don't label it as
          // one. Use the type-specific metadata summary (real row/insert counts) when
          // available; otherwise a plain completion message rather than a fabricated
          // number.
          const summary = this.jobMetadataSummary(job);
          this.dataIngestSuccess = summary ? `Ingest completed. ${summary}.` : 'Ingest completed.';
        } else if (job.status === 'failed') {
          this.dataIngestError = job.errorMessage || 'Ingest failed.';
        } else if (job.status === 'cancelled') {
          this.dataIngestError = 'Ingest was cancelled.';
        }
      },
      error: () => {
        this.dataIngestError = 'Failed to check ingest status after several retries — the ingest may still be running. Check the Jobs page.';
      },
    });
  }

  private stopDataIngestPoll() {
    this.dataIngestPollSub?.unsubscribe();
    this.dataIngestPollSub = null;
  }

  cancelDataIngest() {
    if (!this.dataIngestJob) return;
    const jobId = this.dataIngestJob.id;
    this.adminService.cancelJob(jobId).subscribe({
      next: (job) => {
        this.dataIngestJob = job;
        this.stopDataIngestPoll();
        this.dataIngestError = 'Ingest was cancelled.';
      },
      error: () => {
        this.dataIngestError = 'Failed to cancel ingest.';
      },
    });
  }

  loadUsers() {
    this.usersLoading = true;
    this.usersError = '';
    this.adminService.listUsers().subscribe({
      next: (users) => {
        this.users = users;
        this.usersLoading = false;
      },
      error: () => {
        this.usersLoading = false;
        this.usersError = 'Failed to load users.';
      },
    });
  }

  loadSignedCount() {
    this.signedCountLoading = true;
    this.signedCountError = '';
    this.adminService.getSignedCount().subscribe({
      next: (res) => {
        this.signedCount = res.signedCount;
        this.signedCountLoading = false;
      },
      error: () => {
        this.signedCountLoading = false;
        this.signedCountError = 'Failed to load signed count.';
      },
    });
  }

  submitEvidencePacket() {
    const county = this.evidenceCounty;
    const accountNumber = this.evidenceAccountNumber.trim();
    const salesCompAccountNumbers = this.evidenceSalesComps
      .split(/[\n,]+/)
      .map(s => s.trim())
      .filter(Boolean);
    const equityCompAccountNumbers = this.evidenceEquityComps
      .split(/[\n,]+/)
      .map(s => s.trim())
      .filter(Boolean);
    if (!county || !accountNumber || (salesCompAccountNumbers.length === 0 && equityCompAccountNumbers.length === 0)) return;
    this.evidenceLoading = true;
    this.evidenceError = '';
    this.evidenceSuccess = '';
    this.adminService.generateEvidencePacket({county, year: '2026', accountNumber, salesCompAccountNumbers, equityCompAccountNumbers}).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `evidence-packet-${accountNumber}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        this.evidenceLoading = false;
        this.evidenceSuccess = 'Evidence packet downloaded.';
      },
      error: () => {
        this.evidenceLoading = false;
        this.evidenceError = 'Failed to generate evidence packet. Check the parcel IDs and try again.';
      },
    });
  }

  onEvidenceBatchCsvFile(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      this.evidenceBatchCsv = String(reader.result ?? '');
      this.evidenceBatchParseError = '';
    };
    reader.onerror = () => {
      this.evidenceBatchParseError = 'Failed to read CSV file.';
    };
    reader.readAsText(file);
    input.value = '';
  }

  private parseEvidenceBatchCsv(): { packets: { accountNumber: string; salesCompAccountNumbers: string[]; equityCompAccountNumbers: string[] }[]; error: string } {
    const expectedCols = 1 + BATCH_SALES_COLS + BATCH_EQUITY_COLS;
    const rawLines = this.evidenceBatchCsv.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);
    if (rawLines.length === 0) {
      return { packets: [], error: 'CSV is empty.' };
    }
    let dataLines = rawLines;
    const firstCell = rawLines[0].split(',')[0].trim().toLowerCase();
    if (firstCell === 'subject') {
      dataLines = rawLines.slice(1);
    }
    if (dataLines.length === 0) {
      return { packets: [], error: 'CSV has a header but no data rows.' };
    }
    const packets: { accountNumber: string; salesCompAccountNumbers: string[]; equityCompAccountNumbers: string[] }[] = [];
    for (let i = 0; i < dataLines.length; i++) {
      const cols = dataLines[i].split(',').map(c => c.trim());
      if (cols.length !== expectedCols) {
        return { packets: [], error: `Row ${i + 1} has ${cols.length} columns; expected ${expectedCols} (subject + ${BATCH_SALES_COLS} sales + ${BATCH_EQUITY_COLS} equity).` };
      }
      const accountNumber = cols[0];
      if (!accountNumber) {
        return { packets: [], error: `Row ${i + 1} is missing a subject parcel ID.` };
      }
      const salesCompAccountNumbers = cols.slice(1, 1 + BATCH_SALES_COLS).filter(Boolean);
      const equityCompAccountNumbers = cols.slice(1 + BATCH_SALES_COLS).filter(Boolean);
      if (salesCompAccountNumbers.length === 0 && equityCompAccountNumbers.length === 0) {
        return { packets: [], error: `Row ${i + 1} (${accountNumber}) has no sales or equity comps.` };
      }
      packets.push({ accountNumber, salesCompAccountNumbers, equityCompAccountNumbers });
    }
    return { packets, error: '' };
  }

  private buildEvidenceBatchPayload() {
    const county = this.evidenceBatchCounty;
    const year = this.evidenceBatchYear.trim();
    if (!county || !year) return null;
    const parsed = this.parseEvidenceBatchCsv();
    if (parsed.error || parsed.packets.length === 0) return null;
    return { county, year, packets: parsed.packets };
  }

  get evidenceBatchPreviewCount(): number {
    return this.parseEvidenceBatchCsv().packets.length;
  }

  get evidenceBatchCanSubmit(): boolean {
    return this.buildEvidenceBatchPayload() !== null;
  }

  validateEvidenceBatchCsv() {
    const parsed = this.parseEvidenceBatchCsv();
    this.evidenceBatchParseError = parsed.error;
  }

  submitEvidenceBatch() {
    const parsed = this.parseEvidenceBatchCsv();
    if (parsed.error) {
      this.evidenceBatchParseError = parsed.error;
      return;
    }
    const county = this.evidenceBatchCounty;
    const year = this.evidenceBatchYear.trim();
    if (!county || !year || parsed.packets.length === 0) return;
    const payload = { county, year, packets: parsed.packets };
    this.evidenceBatchParseError = '';
    this.evidenceBatchStarting = true;
    this.evidenceBatchError = '';
    this.evidenceBatchSuccess = '';
    this.evidenceBatchJob = null;
    this.stopEvidenceBatchPoll();
    this.adminService.startEvidencePacketBatch(payload).subscribe({
      next: (res) => {
        this.evidenceBatchStarting = false;
        this.pollEvidenceBatchStatus(res.jobId);
      },
      error: () => {
        this.evidenceBatchStarting = false;
        this.evidenceBatchError = 'Failed to start batch. Check the inputs and try again.';
      },
    });
  }

  private pollEvidenceBatchStatus(jobId: string) {
    this.evidenceBatchPollSub = this.adminService.getJobStatus(jobId).pipe(
      expand(() => this.adminService.getJobStatus(jobId).pipe(delay(5000))),
      takeWhile((job) => job.status !== 'completed' && job.status !== 'failed' && job.status !== 'cancelled', true),
    ).subscribe({
      next: (job) => {
        this.evidenceBatchJob = job;
        if (job.status === 'completed') {
          this.downloadEvidenceBatchZip(job.id);
        } else if (job.status === 'failed') {
          this.evidenceBatchError = job.errorMessage || 'Evidence batch failed.';
        } else if (job.status === 'cancelled') {
          this.evidenceBatchError = 'Evidence batch was cancelled.';
        }
      },
      error: () => {
        this.evidenceBatchError = 'Failed to check batch status.';
      },
    });
  }

  private stopEvidenceBatchPoll() {
    this.evidenceBatchPollSub?.unsubscribe();
    this.evidenceBatchPollSub = null;
  }

  cancelEvidenceBatch() {
    if (!this.evidenceBatchJob) return;
    const jobId = this.evidenceBatchJob.id;
    this.adminService.cancelJob(jobId).subscribe({
      next: (job) => {
        this.evidenceBatchJob = job;
        this.stopEvidenceBatchPoll();
        this.evidenceBatchError = 'Evidence batch was cancelled.';
      },
      error: () => {
        this.evidenceBatchError = 'Failed to cancel evidence batch.';
      },
    });
  }

  downloadEvidenceBatchZip(jobId: string) {
    this.evidenceBatchDownloading = true;
    this.evidenceBatchError = '';
    this.adminService.downloadEvidencePacketBatch(jobId).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `evidence-packets-${jobId}.zip`;
        a.click();
        URL.revokeObjectURL(url);
        this.evidenceBatchDownloading = false;
        this.evidenceBatchSuccess = 'Evidence batch ZIP downloaded.';
      },
      error: () => {
        this.evidenceBatchDownloading = false;
        this.evidenceBatchError = 'Failed to download the batch ZIP.';
      },
    });
  }

  evidenceBatchMetadata(job: JobStatus | null): { year?: string; totalPackets?: number; packetsCompleted?: number } {
    return (job?.metadata ?? {}) as { year?: string; totalPackets?: number; packetsCompleted?: number };
  }

  querySteps() {
    const county = this.stepsCounty;
    const parcelIds = this.stepsInput
      .split(/[\n,]+/)
      .map(s => s.trim())
      .filter(Boolean);
    if (!county || parcelIds.length === 0) return;
    this.stepsLoading = true;
    this.stepsError = '';
    this.stepsAccounts = [];
    this.stepUpdateSuccess = '';
    this.stepUpdateError = '';
    this.adminService.queryCaseSteps(parcelIds.map(parcelId => ({ county, parcelId }))).subscribe({
      next: (res) => {
        this.stepsAccounts = res.query;
        this.stepsLoading = false;
      },
      error: () => {
        this.stepsLoading = false;
        this.stepsError = 'Failed to query case steps. Check the county and parcel IDs and try again.';
      },
    });
  }

  updateStepStatus(step: CaseStep, newStatus: 'complete' | 'active' | 'pending') {
    if (step.status === newStatus) return;
    this.stepUpdating = step.id;
    this.stepUpdateError = '';
    this.stepUpdateSuccess = '';
    this.adminService.patchCaseSteps([{ id: step.id, status: newStatus }]).subscribe({
      next: (res) => {
        this.stepUpdating = null;
        this.stepUpdateSuccess = `Step "${step.title}" updated to ${newStatus}.`;
        this.applyStepUpdates(res.steps);
      },
      error: () => {
        this.stepUpdating = null;
        this.stepUpdateError = `Failed to update step "${step.title}".`;
      },
    });
  }

  bulkUpdateSteps(newStatus: 'complete' | 'active' | 'pending') {
    const steps = this.allStepsFlat.filter(s => s.status !== newStatus);
    if (steps.length === 0) return;
    this.bulkUpdating = true;
    this.stepUpdateError = '';
    this.stepUpdateSuccess = '';
    this.adminService.patchCaseSteps(steps.map(s => ({ id: s.id, status: newStatus }))).subscribe({
      next: (res) => {
        this.bulkUpdating = false;
        this.stepUpdateSuccess = `${steps.length} step(s) updated to ${newStatus}.`;
        this.applyStepUpdates(res.steps);
      },
      error: () => {
        this.bulkUpdating = false;
        this.stepUpdateError = 'Failed to bulk update steps.';
      },
    });
  }

  private applyStepUpdates(updated: CaseStep[]) {
    for (const account of this.stepsAccounts) {
      for (let i = 0; i < account.steps.length; i++) {
        const match = updated.find(s => s.id === account.steps[i].id);
        if (match) {
          account.steps[i] = match;
        }
      }
    }
  }

  loadDuplicateEmails() {
    this.dupLoading = true;
    this.dupError = '';
    this.adminService.getDuplicateEmails().subscribe({
      next: (res) => {
        this.dupGroups = res.duplicates;
        this.dupLoading = false;
      },
      error: () => {
        this.dupLoading = false;
        this.dupError = 'Failed to load duplicate emails.';
      },
    });
  }

  runDedup() {
    this.dedupLoading = true;
    this.dedupResult = null;
    this.dedupError = '';
    this.adminService.deduplicateEmails().subscribe({
      next: (res) => {
        this.dedupLoading = false;
        this.dedupResult = res;
        this.dupGroups = [];
      },
      error: () => {
        this.dedupLoading = false;
        this.dedupError = 'Failed to deduplicate emails. Please try again.';
      },
    });
  }

  toggleAdmin(user: AdminUser) {
    this.adminActionLoading = user.id;
    this.adminActionError = null;
    const action = user.isAdmin
      ? this.adminService.demoteUser(user.email)
      : this.adminService.promoteUser(user.email);
    action.subscribe({
      next: () => {
        user.isAdmin = !user.isAdmin;
        this.adminActionLoading = null;
      },
      error: () => {
        this.adminActionLoading = null;
        this.adminActionError = `Failed to ${user.isAdmin ? 'demote' : 'promote'} ${user.email}.`;
      },
    });
  }

  downloadUserDocuments(user: AdminUser) {
    this.downloadError = null;
    this.adminService.downloadUserDocuments(user.email, () => {
      this.downloadError = `Failed to download documents for ${user.email}.`;
    });
  }

  downloadAllDocuments() {
    this.downloadAllError = '';
    this.adminService.downloadAllDocuments(() => {
      this.downloadAllError = 'Failed to download documents. Please try again.';
    });
  }

  submitLinkEmail() {
    const email = this.linkEmail.trim().toLowerCase();
    const county = this.linkCounty;
    const parcelId = this.linkParcelId.trim();
    if (!email || !county || !parcelId) return;
    this.linkLoading = true;
    this.linkSuccess = null;
    this.linkError = '';
    this.adminService.linkEmail({email, parcelId, county, taxYear: this.linkTaxYear}).subscribe({
      next: (res) => {
        this.linkLoading = false;
        this.linkSuccess = res;
        this.linkEmail = '';
        this.linkParcelId = '';
        // Deliberately a separate call, not a side effect of the association above (see
        // AdminService.syncCaseJurisdiction) — if the user already has a case (e.g. from
        // an earlier, different-jurisdiction account), it needs to be re-pointed at this
        // newly-linked property so its compliance copy/deadline stop reflecting the old
        // jurisdiction. Retries a few times; if it still fails, reverts the association
        // itself rather than leaving the account linked with a stale case.
        this.syncCaseAfterLink(email, county, parcelId);
      },
      error: () => {
        this.linkLoading = false;
        this.linkError = 'Failed to link email. Check the email, county and parcel ID and try again.';
      },
    });
  }

  private static readonly CASE_SYNC_MAX_ATTEMPTS = 3;

  private syncCaseAfterLink(email: string, county: PublicCounty, parcelId: string, attempt = 1) {
    this.adminService.syncCaseJurisdiction(email).subscribe({
      next: () => {
        // synced (or nothing to sync — no case exists yet, which is fine)
      },
      error: () => {
        if (attempt < AdminComponent.CASE_SYNC_MAX_ATTEMPTS) {
          this.syncCaseAfterLink(email, county, parcelId, attempt + 1);
          return;
        }
        // Retries exhausted — revert the association so we don't leave the account
        // linked with a case still showing the wrong jurisdiction's copy/deadline.
        this.adminService.unlinkEmail(email, county, parcelId).subscribe({
          next: () => {
            this.linkSuccess = null;
            this.linkError = `Linked ${email} to parcel ${parcelId} in ${county}, but couldn't update their existing case to match after ${AdminComponent.CASE_SYNC_MAX_ATTEMPTS} attempts — the link has been reverted. Please try again.`;
          },
          error: () => {
            this.linkError = `Linked ${email} to parcel ${parcelId} in ${county} and updating their case failed, but automatically reverting the link ALSO failed — this account is now linked with a stale case and needs manual cleanup.`;
          },
        });
      },
    });
  }

  loadJobs() {
    this.jobsLoading = true;
    this.jobsError = '';
    this.cancelError = '';
    this.adminService.listJobs().subscribe({
      next: (jobs) => {
        this.recentJobs = jobs;
        this.jobsLoading = false;
      },
      error: () => {
        this.jobsLoading = false;
        this.jobsError = 'Failed to load recent jobs.';
      },
    });
  }

  private startJobsPoll() {
    if (this.jobsPollSub) return;
    this.jobsPollSub = interval(this.jobsPollIntervalMs).pipe(
      switchMap(() => this.adminService.listJobs()),
    ).subscribe({
      next: (jobs) => {
        this.recentJobs = jobs;
        this.jobsError = '';
      },
      error: () => {
        this.jobsError = 'Failed to refresh recent jobs.';
      },
    });
  }

  private stopJobsPoll() {
    this.jobsPollSub?.unsubscribe();
    this.jobsPollSub = null;
  }

  cancelJob(job: JobStatus) {
    this.cancelingJob = job.id;
    this.cancelError = '';
    this.adminService.cancelJob(job.id).subscribe({
      next: (updated) => {
        this.cancelingJob = null;
        const idx = this.recentJobs.findIndex(j => j.id === updated.id);
        if (idx >= 0) {
          this.recentJobs[idx] = updated;
        }
      },
      error: () => {
        this.cancelingJob = null;
        this.cancelError = `Failed to cancel job ${job.id}.`;
      },
    });
  }

  jobIsActive(job: JobStatus): boolean {
    return job.status !== 'completed' && job.status !== 'failed' && job.status !== 'cancelled';
  }

  jobTypeLabel(job: JobStatus): string {
    const t = job.type as string;
    if (t === 'atom_upload') return 'ATOM Upload';
    if (t === 'atom_ingest') return 'ATOM Ingest';
    if (t === 'evidence_packet_batch') return 'Evidence Batch';
    if (t === 'county_data_upload') return 'County Data Upload';
    if (t === 'county_data_ingest') return 'County Data Ingest';
    if (t === 'parcels_list_csv_upload') return 'Parcels List CSV Upload';
    return t;
  }

  toggleJobDetails(job: JobStatus) {
    this.expandedJobId = this.expandedJobId === job.id ? null : job.id;
  }

  // metadata is an untrusted JSON bag: `errors` may be absent, a string, or garbage.
  jobRowErrorCount(job: JobStatus): number {
    const raw = (job.metadata as { errors?: unknown } | null | undefined)?.errors;
    const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  }

  jobRowErrorDetails(job: JobStatus): string[] {
    const raw = (job.metadata as { errorDetails?: unknown } | null | undefined)?.errorDetails;
    if (!Array.isArray(raw)) return [];
    return raw.filter((d): d is string => typeof d === 'string' && d.trim().length > 0);
  }

  // errorDetails is capped by the backend while `errors` holds the true count.
  jobRowErrorTruncationNote(job: JobStatus): string {
    const total = this.jobRowErrorCount(job);
    const shown = this.jobRowErrorDetails(job).length;
    return shown > 0 && shown < total ? `Showing ${shown} of ${total}` : '';
  }

  jobMetadataJson(job: JobStatus): string {
    return JSON.stringify(job.metadata ?? {}, null, 2);
  }

  jobMetadataSummary(job: JobStatus): string {
    const jobType = job.type as string;
    if (jobType === 'county_data_upload' || jobType === 'parcels_list_csv_upload') {
      const meta = job.metadata as { county?: string };
      const parts: string[] = [];
      if (meta.county) parts.push(`county ${meta.county}`);
      return parts.join(' · ');
    }
    if (jobType === 'county_data_ingest') {
      const meta = job.metadata as { county?: string; rowsProcessed?: number; totalRows?: number; inserted?: number };
      const parts: string[] = [];
      if (meta.county) parts.push(`county ${meta.county}`);
      if (meta.rowsProcessed != null && meta.totalRows != null) parts.push(`${meta.rowsProcessed}/${meta.totalRows} rows`);
      if (meta.inserted != null) parts.push(`${meta.inserted} inserted`);
      return parts.join(' · ');
    }
    if (jobType === 'county_generic_ingest') {
      return this.countyMappingIngestSummary(job);
    }
    if (jobType === 'county_generic_fetch') {
      return this.countyMappingFetchSummary(job);
    }
    return '';
  }

  ngOnDestroy() {
    this.stopDataUploadPoll();
    this.stopDataIngestPoll();
    this.stopEvidenceBatchPoll();
    this.stopJobsPoll();
    this.stopCountyMappingFetchPoll();
    this.stopCountyMappingIngestPoll();
  }

  searchUser() {
    const email = this.lookupEmail.trim().toLowerCase();
    if (!email) return;
    this.lookupLoading = true;
    this.lookupError = '';
    this.lookupUser = null;
    this.addressDeleteError = '';
    this.lookupPayments = null;
    this.lookupPaymentsError = '';
    this.lookupIneligibleKnown = false;
    this.lookupIneligibleAt = null;
    this.lookupIneligibleError = '';
    this.adminService.getUser(email).subscribe({
      next: (user) => {
        this.lookupLoading = false;
        this.lookupUser = user;
        this.loadUserPayments(email);
        this.loadPartnerIneligible(email);
      },
      error: (err) => {
        this.lookupLoading = false;
        this.lookupError = err.status === 404
          ? `No user found with email ${email}.`
          : 'Failed to look up user. Please try again.';
      },
    });
  }

  deleteAddress(addressId: string) {
    if (!this.lookupUser) return;
    const email = this.lookupUser.email;
    this.addressDeleting = addressId;
    this.addressDeleteError = '';
    this.adminService.deleteAddress(email, addressId).subscribe({
      next: () => {
        this.addressDeleting = null;
        if (this.lookupUser) {
          this.lookupUser = {
            ...this.lookupUser,
            addresses: this.lookupUser.addresses.filter(a => a.id !== addressId),
          };
        }
      },
      error: () => {
        this.addressDeleting = null;
        this.addressDeleteError = 'Failed to delete address. Please try again.';
      },
    });
  }

  /** A linked parcel's key: a parcel ID is only unique within its county. */
  propertyKey(prop: UserDetailProperty): string {
    return `${prop.countyCode}:${prop.parcelId}`;
  }

  deleteProperty(prop: UserDetailProperty) {
    if (!this.lookupUser) return;
    const email = this.lookupUser.email;
    const key = this.propertyKey(prop);
    this.propertyDeleting = key;
    this.propertyDeleteError = '';
    this.adminService.unlinkEmail(email, prop.countyCode, prop.parcelId).subscribe({
      next: () => {
        this.propertyDeleting = null;
        if (this.lookupUser) {
          this.lookupUser = {
            ...this.lookupUser,
            properties: this.lookupUser.properties.filter(p => this.propertyKey(p) !== key),
          };
        }
      },
      error: () => {
        this.propertyDeleting = null;
        this.propertyDeleteError = 'Failed to unlink the parcel. Please try again.';
      },
    });
  }

  deleteSignatureRequests() {
    const email = this.sigEmail.trim().toLowerCase();
    if (!email) return;
    this.sigDeleting = true;
    this.sigSuccess = '';
    this.sigError = '';
    this.adminService.deleteSignatureRequests(email).subscribe({
      next: () => {
        this.sigDeleting = false;
        this.sigSuccess = `Signature requests deleted for ${email}.`;
        this.sigEmail = '';
      },
      error: () => {
        this.sigDeleting = false;
        this.sigError = 'Failed to delete signature requests. Check the email and try again.';
      },
    });
  }

  // A user who has never paid is a valid answer, not an error: an empty payments array
  // renders the empty state, and only a failed request sets lookupPaymentsError.
  private loadUserPayments(email: string) {
    this.lookupPaymentsLoading = true;
    this.lookupPaymentsError = '';
    this.adminService.getUserPayments(email).subscribe({
      next: (payments) => {
        this.lookupPaymentsLoading = false;
        this.lookupPayments = payments;
      },
      error: () => {
        this.lookupPaymentsLoading = false;
        this.lookupPaymentsError = 'Failed to load payments for this user. Please try again.';
      },
    });
  }

  // A null `partnerIneligibleAt` is the ordinary answer — not marked — so it sets `known` just
  // as a timestamp does. Only a failed request leaves the state unknown, and it says so: an
  // unread flag is never rendered as eligible.
  private loadPartnerIneligible(email: string) {
    this.lookupIneligibleLoading = true;
    this.lookupIneligibleError = '';
    this.adminService.getPartnerIneligible(email).subscribe({
      next: (state) => {
        this.lookupIneligibleLoading = false;
        this.lookupIneligibleKnown = true;
        this.lookupIneligibleAt = state.partnerIneligibleAt;
      },
      error: () => {
        this.lookupIneligibleLoading = false;
        this.lookupIneligibleKnown = false;
        this.lookupIneligibleAt = null;
        this.lookupIneligibleError = 'Failed to read partner eligibility for this user. Please try again.';
      },
    });
  }

  resetPartnerState() {
    const email = this.resetEmail.trim().toLowerCase();
    if (!email) return;
    this.resetting = true;
    this.resetResult = null;
    this.resetError = '';
    this.adminService.resetPartnerState(email).subscribe({
      next: (result) => {
        this.resetting = false;
        this.resetResult = result;
        this.resetEmail = '';
        // Keep the lookup view honest if it is showing the user we just reset.
        if (this.lookupUser?.email === email) {
          this.loadUserPayments(email);
        }
      },
      error: () => {
        // Deliberately leaves resetEmail intact so the operator can retry without retyping.
        this.resetting = false;
        this.resetError = 'Failed to reset partner state. Check the email and try again.';
      },
    });
  }


  searchPartnerLead() {
    const email = this.leadEmail.trim().toLowerCase();
    if (!email || this.leadLoading) return;
    this.leadLoading = true;
    this.leadResult = null;
    this.leadError = '';
    this.adminService.getPartnerLead(email).subscribe({
      next: (result) => {
        this.leadLoading = false;
        this.leadResult = result;
      },
      error: () => {
        this.leadLoading = false;
        this.leadError = 'Could not read partner leads. This is a request failure, not an answer — it does not mean the lead is missing. Please try again.';
      },
    });
  }

  // Nullable columns are common in the import; these keep a literal "null" out of the DOM.
  leadName(lead: AdminPartnerLead): string {
    const name = [lead.firstName, lead.lastName].filter(Boolean).join(' ').trim();
    return name || 'not provided';
  }

  leadMoney(amount: number | null): string {
    return amount === null || amount === undefined ? 'not provided' : `$${amount.toLocaleString('en-US')}`;
  }

  markPartnerIneligible() {
    this.submitPartnerIneligible('mark');
  }

  clearPartnerIneligible() {
    this.submitPartnerIneligible('unmark');
  }

  /**
   * Both directions, one body: they differ only in the call and the failure wording. The email
   * survives either outcome — after marking, un-mark is the very next thing an operator might
   * need, and after a failure retyping it is pure friction.
   */
  private submitPartnerIneligible(direction: 'mark' | 'unmark') {
    const email = this.ineligibleEmail.trim().toLowerCase();
    if (!email || this.ineligibleBusy) return;
    this.ineligibleBusy = direction;
    this.ineligibleResult = null;
    this.ineligibleError = '';
    const call = direction === 'mark'
      ? this.adminService.markPartnerIneligible(email)
      : this.adminService.clearPartnerIneligible(email);
    call.subscribe({
      next: (result) => {
        this.ineligibleBusy = null;
        this.ineligibleResult = result;
        // The endpoint returns the resulting state, including on a no-op, so the lookup above
        // can be updated in place instead of re-reading it.
        if (result.email.toLowerCase() === this.lookedUpEmail) {
          this.lookupIneligibleKnown = true;
          this.lookupIneligibleAt = result.partnerIneligibleAt;
          this.lookupIneligibleError = '';
        }
      },
      error: () => {
        this.ineligibleBusy = null;
        this.ineligibleError = direction === 'mark'
          ? 'Failed to mark this user ineligible. Check the email and try again.'
          : 'Failed to clear the ineligible flag. Check the email and try again.';
      },
    });
  }

  /** The email of the user currently shown in Look Up User, lower-cased, if any. */
  private get lookedUpEmail(): string | null {
    return this.lookupUser?.email?.toLowerCase() ?? this.lookupPayments?.email?.toLowerCase() ?? null;
  }


  // County Mapping panel (prototype)

  loadCountyMappingStateGroups() {
    this.countyMappingStateGroupsError = '';
    this.adminService.listCountyIngestConfigs().subscribe({
      next: (configs) => {
        const byState = new Map<string, CountyMappingListEntry[]>();
        for (const config of configs) {
          const entry: CountyMappingListEntry = {
            jurisdictionCode: config.jurisdictionCode,
            label: COUNTY_MAPPING_LABELS[config.jurisdictionCode] ?? config.jurisdictionCode,
          };
          const stateName = config.state ?? 'Unassigned';
          byState.set(stateName, [...(byState.get(stateName) ?? []), entry]);
        }
        this.countyMappingStateGroups = Array.from(byState.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([stateName, entries]) => ({
            stateName,
            entries: entries.sort((a, b) => a.label.localeCompare(b.label)),
          }));
      },
      error: (err) => {
        this.countyMappingStateGroupsError = err?.error?.message ?? 'Failed to load counties from server.';
      },
    });
  }

  onCountyMappingTemplateChange(value: string | string[] | null | undefined) {
    if (typeof value === 'string') this.selectCountyMappingEntry(value);
  }

  selectCountyMappingEntry(jurisdictionCodeOrBlank: string) {
    if (jurisdictionCodeOrBlank === 'blank') {
      this.startBlankCountyMapping();
      return;
    }
    const group = this.countyMappingStateGroups.find((g) =>
      g.entries.some((e) => e.jurisdictionCode === jurisdictionCodeOrBlank),
    );
    this.countyMappingTemplate = jurisdictionCodeOrBlank;
    this.countyMappingServerJurisdiction = jurisdictionCodeOrBlank;
    this.countyMappingServerState = group?.stateName ?? '';
    this.countyMappingSourceFiles = [];
    this.countyMappingSourceFileColumns = {};
    this.countyMappingSourceFileDelimiters = {};
    this.countyMappingSourceFileUrls = {};
    this.countyMappingFields = this.buildCountyMappingFields();
    this.countyMappingSelectedField = null;
    this.loadCountyMappingFromServer();
  }

  private buildCountyMappingFields(lookup?: (kinField: string) => CountyFieldMapping | undefined): Record<string, CountyFieldMapping> {
    return Object.fromEntries(
      KIN_FIELDS.map((f) => {
        const existing = lookup?.(f.key);
        return [f.key, existing ? {transformType: existing.transformType, params: [...existing.params]} : {transformType: 'copy' as TransformType, params: []}];
      }),
    );
  }

  startBlankCountyMapping() {
    this.countyMappingTemplate = 'blank';
    this.countyMappingServerJurisdiction = '';
    this.countyMappingServerState = '';
    this.countyMappingStagedLocation = '';
    this.countyMappingServerStatus = '';
    this.countyMappingServerError = '';
    this.countyMappingSourceFiles = [];
    this.countyMappingSourceFileColumns = {};
    this.countyMappingSourceFileDelimiters = {};
    this.countyMappingSourceFileUrls = {};
    this.countyMappingFields = this.buildCountyMappingFields();
    this.countyMappingSelectedField = null;
  }

  countyMappingServerJurisdiction = '';
  countyMappingServerState = '';
  countyMappingServerBusy = false;
  countyMappingServerError = '';
  countyMappingServerStatus = '';
  countyMappingStagedLocation = '';
  countyMappingFetchUrl = '';
  countyMappingFetchJob: JobStatus | null = null;
  countyMappingFetchError = '';
  countyMappingFetchStarting = false;
  private countyMappingFetchPollSub: Subscription | null = null;
  countyMappingIngestJob: JobStatus | null = null;
  countyMappingIngestError = '';
  countyMappingIngestStarting = false;
  private countyMappingIngestPollSub: Subscription | null = null;

  private static isJobActive(job: JobStatus | null): boolean {
    return !!job && job.status !== 'completed' && job.status !== 'failed' && job.status !== 'cancelled';
  }

  get isCountyMappingFetchActive(): boolean {
    return AdminComponent.isJobActive(this.countyMappingFetchJob);
  }

  get isCountyMappingIngestActive(): boolean {
    return AdminComponent.isJobActive(this.countyMappingIngestJob);
  }

  loadCountyMappingFromServer() {
    this.countyMappingServerBusy = true;
    this.countyMappingServerError = '';
    this.countyMappingServerStatus = '';
    this.adminService.getCountyIngestConfig(this.countyMappingServerJurisdiction).subscribe({
      next: (config) => {
        this.countyMappingSourceFiles = config.sourceFiles.map((f) => f.fileName);
        this.countyMappingSourceFileColumns = Object.fromEntries(
          config.sourceFiles.map((f) => [f.fileName, f.columns]),
        );
        this.countyMappingSourceFileDelimiters = Object.fromEntries(
          config.sourceFiles.map((f) => [f.fileName, f.delimiter]),
        );
        this.countyMappingSourceFileUrls = Object.fromEntries(
          config.sourceFiles.filter((f) => f.dataLocationUrl).map((f) => [f.fileName, f.dataLocationUrl as string]),
        );
        this.countyMappingFields = this.buildCountyMappingFields((key) => {
          const existing = config.fieldMappings.find((m) => m.kinField === key);
          return existing ? {transformType: existing.transformType as TransformType, params: existing.params} : undefined;
        });
        this.countyMappingSelectedField = null;
        this.countyMappingServerState = config.state ?? '';
        this.countyMappingStagedLocation = config.stagedS3Prefix
          ? `s3://${config.stagedS3Prefix}`
          : config.stagedLocalPath ?? '';
        this.countyMappingServerStatus = `Loaded ${config.sourceFiles.length} source files, ${config.fieldMappings.length} field mappings from server.`;
        this.countyMappingServerBusy = false;
      },
      error: (err) => {
        this.countyMappingServerError = err?.error?.message ?? 'Failed to load config from server.';
        this.countyMappingServerBusy = false;
      },
    });
  }

  private buildCountyMappingPayload() {
    return {
      state: this.countyMappingServerState.trim() || null,
      sourceFiles: this.countyMappingSourceFiles.map((fileName) => ({
        fileName,
        dataLocationUrl: this.countyMappingSourceFileUrls[fileName] ?? null,
        delimiter: this.countyMappingSourceFileDelimiters[fileName] ?? 'comma',
        columns: this.countyMappingSourceFileColumns[fileName] ?? [],
      })),
      // Optional trailing params (e.g. a row filter) left blank are dropped rather than
      // saved as '', which the ingest would treat as a real (empty) column name.
      fieldMappings: KIN_FIELDS.map((f) => {
        const mapping = this.countyMappingFields[f.key];
        const specs = TRANSFORM_TYPE_OPTIONS.find((t) => t.value === mapping?.transformType)?.params;
        return {f, params: trimTrailingBlankParams(mapping?.params ?? [], specs)};
      })
        .filter(({params}) => params.length > 0)
        .map(({f, params}) => ({
          kinField: f.key,
          transformType: this.countyMappingFields[f.key].transformType,
          params,
        })),
    };
  }

  saveCountyMappingToServer() {
    this.countyMappingServerBusy = true;
    this.countyMappingServerError = '';
    this.countyMappingServerStatus = '';
    this.adminService.saveCountyIngestConfig(this.countyMappingServerJurisdiction, this.buildCountyMappingPayload()).subscribe({
      next: (config) => {
        this.countyMappingServerStatus = `Saved ${config.sourceFiles.length} source files, ${config.fieldMappings.length} field mappings to server.`;
        this.countyMappingServerBusy = false;
        this.loadCountyMappingStateGroups();
      },
      error: (err) => {
        this.countyMappingServerError = err?.error?.message ?? 'Failed to save config to server.';
        this.countyMappingServerBusy = false;
      },
    });
  }

  uploadCountyLayout(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (this.countyMappingLayoutBusy) return;
    if (!this.countyMappingServerJurisdiction.trim()) {
      this.countyMappingLayoutError = 'Enter a jurisdiction code first.';
      return;
    }
    this.countyMappingLayoutBusy = true;
    this.countyMappingLayoutError = '';
    this.countyMappingLayoutStatus = '';
    this.adminService.applyCountyLayout(this.countyMappingServerJurisdiction, file).subscribe({
      next: (result) => {
        this.countyMappingLayoutBusy = false;
        const parts: string[] = [];
        if (result.applied.length > 0) parts.push(`Applied to ${result.applied.join(', ')}.`);
        if (result.unmatched.length > 0) parts.push(`No staged file matched: ${result.unmatched.join(', ')}.`);
        this.countyMappingLayoutStatus = parts.join(' ') || 'No matching sheets found in that layout file.';
        this.loadCountyMappingFromServer();
      },
      error: (err) => {
        this.countyMappingLayoutError = err?.error?.message ?? 'Failed to apply layout file.';
        this.countyMappingLayoutBusy = false;
      },
    });
  }

  startCountyMappingFetch(url?: string) {
    this.countyMappingFetchError = '';
    if (!this.countyMappingServerJurisdiction.trim()) {
      this.countyMappingFetchError = 'Enter a jurisdiction code first.';
      return;
    }
    this.countyMappingFetchStarting = true;
    this.countyMappingFetchJob = null;
    this.stopCountyMappingFetchPoll();
    this.adminService.saveCountyIngestConfig(this.countyMappingServerJurisdiction, this.buildCountyMappingPayload()).subscribe({
      next: () => {
        this.adminService.fetchCountyIngestConfig(this.countyMappingServerJurisdiction, url).subscribe({
          next: (res) => {
            this.countyMappingFetchStarting = false;
            this.countyMappingFetchUrl = '';
            this.pollCountyMappingFetchStatus(res.jobId);
          },
          error: (err) => {
            this.countyMappingFetchStarting = false;
            this.countyMappingFetchError = err?.error?.message ?? 'Failed to start fetch.';
          },
        });
      },
      error: (err) => {
        this.countyMappingFetchStarting = false;
        this.countyMappingFetchError = err?.error?.message ?? 'Failed to save config before fetching.';
      },
    });
  }

  fetchCountyMappingUrl() {
    const url = this.countyMappingFetchUrl.trim();
    if (!url) {
      this.countyMappingFetchError = 'Paste a URL first.';
      return;
    }
    this.startCountyMappingFetch(url);
  }

  fetchAllCountyMappingUrls() {
    this.startCountyMappingFetch();
  }

  private pollCountyMappingFetchStatus(jobId: string) {
    this.countyMappingFetchPollSub = this.adminService.getJobStatus(jobId).pipe(
      retry(AdminComponent.JOB_POLL_RETRY),
      expand(() => this.adminService.getJobStatus(jobId).pipe(delay(5000), retry(AdminComponent.JOB_POLL_RETRY))),
      takeWhile((job) => job.status !== 'completed' && job.status !== 'failed' && job.status !== 'cancelled', true),
    ).subscribe({
      next: (job) => {
        this.countyMappingFetchJob = job;
        if (job.status === 'completed') {
          this.loadCountyMappingFromServer();
        } else if (job.status === 'failed') {
          this.countyMappingFetchError = job.errorMessage || 'Fetch failed.';
        } else if (job.status === 'cancelled') {
          this.countyMappingFetchError = 'Fetch was cancelled.';
        }
      },
      error: () => {
        this.countyMappingFetchError = 'Failed to check fetch status after several retries — the fetch may still be running. Check the Jobs page.';
      },
    });
  }

  private stopCountyMappingFetchPoll() {
    this.countyMappingFetchPollSub?.unsubscribe();
    this.countyMappingFetchPollSub = null;
  }

  cancelCountyMappingFetch() {
    if (!this.countyMappingFetchJob) return;
    const jobId = this.countyMappingFetchJob.id;
    this.adminService.cancelJob(jobId).subscribe({
      next: (job) => {
        this.countyMappingFetchJob = job;
        this.stopCountyMappingFetchPoll();
        this.countyMappingFetchError = 'Fetch was cancelled.';
      },
      error: () => {
        this.countyMappingFetchError = 'Failed to cancel fetch.';
      },
    });
  }

  countyMappingFetchSummary(job: JobStatus | null): string {
    if (!job) return '';
    const meta = job.metadata as {currentFile?: string; filesStaged?: number};
    const parts: string[] = [];
    if (meta.currentFile) parts.push(`fetching ${meta.currentFile}`);
    if (meta.filesStaged != null) parts.push(`${meta.filesStaged} files staged`);
    return parts.join(' · ');
  }

  startCountyMappingIngest() {
    this.countyMappingIngestError = '';
    if (!this.countyMappingServerJurisdiction.trim()) {
      this.countyMappingIngestError = 'Enter a jurisdiction code first.';
      return;
    }
    this.countyMappingIngestStarting = true;
    this.countyMappingIngestJob = null;
    this.stopCountyMappingIngestPoll();
    this.adminService.saveCountyIngestConfig(this.countyMappingServerJurisdiction, this.buildCountyMappingPayload()).subscribe({
      next: () => {
        this.adminService.ingestCountyIngestConfig(this.countyMappingServerJurisdiction).subscribe({
          next: (res) => {
            this.countyMappingIngestStarting = false;
            this.pollCountyMappingIngestStatus(res.jobId);
          },
          error: (err) => {
            this.countyMappingIngestStarting = false;
            this.countyMappingIngestError = err?.error?.message ?? 'Failed to start ingest.';
          },
        });
      },
      error: (err) => {
        this.countyMappingIngestStarting = false;
        this.countyMappingIngestError = err?.error?.message ?? 'Failed to save config before ingesting.';
      },
    });
  }

  private pollCountyMappingIngestStatus(jobId: string) {
    this.countyMappingIngestPollSub = this.adminService.getJobStatus(jobId).pipe(
      retry(AdminComponent.JOB_POLL_RETRY),
      expand(() => this.adminService.getJobStatus(jobId).pipe(delay(5000), retry(AdminComponent.JOB_POLL_RETRY))),
      takeWhile((job) => job.status !== 'completed' && job.status !== 'failed' && job.status !== 'cancelled', true),
    ).subscribe({
      next: (job) => {
        this.countyMappingIngestJob = job;
        if (job.status === 'failed') {
          this.countyMappingIngestError = job.errorMessage || 'Ingest failed.';
        } else if (job.status === 'cancelled') {
          this.countyMappingIngestError = 'Ingest was cancelled.';
        }
      },
      error: () => {
        this.countyMappingIngestError = 'Failed to check ingest status after several retries — the ingest may still be running. Check the Jobs page.';
      },
    });
  }

  private stopCountyMappingIngestPoll() {
    this.countyMappingIngestPollSub?.unsubscribe();
    this.countyMappingIngestPollSub = null;
  }

  cancelCountyMappingIngest() {
    if (!this.countyMappingIngestJob) return;
    const jobId = this.countyMappingIngestJob.id;
    this.adminService.cancelJob(jobId).subscribe({
      next: (job) => {
        this.countyMappingIngestJob = job;
        this.stopCountyMappingIngestPoll();
        this.countyMappingIngestError = 'Ingest was cancelled.';
      },
      error: () => {
        this.countyMappingIngestError = 'Failed to cancel ingest.';
      },
    });
  }

  countyMappingIngestSummary(job: JobStatus | null): string {
    if (!job) return '';
    const meta = job.metadata as {
      phase?: 'staging' | 'computing';
      stagingFileName?: string;
      stagingRowsStaged?: number;
      stagingTotalRows?: number;
      totalRows?: number;
      rowsProcessed?: number;
      inserted?: number;
    };
    if (meta.phase === 'staging' && meta.stagingRowsStaged != null) {
      const staged = meta.stagingRowsStaged;
      const total = meta.stagingTotalRows;
      const label = meta.stagingFileName ? ` (${meta.stagingFileName})` : '';
      if (total) {
        const pct = Math.min(100, Math.round((staged / total) * 100));
        return `staging: ${pct}% (${staged}/${total} rows)${label}`;
      }
      return `staging: ${staged} rows${label}`;
    }
    const parts: string[] = [];
    if (meta.rowsProcessed != null) {
      if (meta.totalRows) {
        const pct = Math.min(100, Math.round((meta.rowsProcessed / meta.totalRows) * 100));
        parts.push(`${pct}% (${meta.rowsProcessed}/${meta.totalRows} rows)`);
      } else {
        parts.push(`${meta.rowsProcessed} rows processed`);
      }
    }
    if (meta.inserted != null) parts.push(`${meta.inserted} inserted`);
    return parts.join(' · ');
  }

  selectCountyMappingField(key: string) {
    this.countyMappingSelectedField = this.countyMappingSelectedField === key ? null : key;
  }

  get countyMappingSelectedFieldLabel(): string {
    return KIN_FIELDS.find((f) => f.key === this.countyMappingSelectedField)?.label ?? '';
  }

  get countyMappingSelectedMapping(): CountyFieldMapping | null {
    if (!this.countyMappingSelectedField) return null;
    return this.countyMappingFields[this.countyMappingSelectedField] ?? null;
  }

  get countyMappingSelectedParamSpecs(): TransformParamSpec[] {
    const mapping = this.countyMappingSelectedMapping;
    if (!mapping) return [];
    return TRANSFORM_TYPE_OPTIONS.find((t) => t.value === mapping.transformType)?.params ?? [];
  }

  get countyMappingSelectedTypeDescription(): string {
    const mapping = this.countyMappingSelectedMapping;
    if (!mapping) return '';
    return TRANSFORM_TYPE_OPTIONS.find((t) => t.value === mapping.transformType)?.description ?? '';
  }

  setFieldTransformType(type: TransformType) {
    const key = this.countyMappingSelectedField;
    if (!key) return;
    this.countyMappingFields[key] = {transformType: type, params: []};
  }

  setFieldParam(paramIndex: number, value: string) {
    const key = this.countyMappingSelectedField;
    if (!key) return;
    const mapping = this.countyMappingFields[key];
    // Fill any skipped params with '' rather than leaving holes, which would save as null.
    const params = Array.from({length: Math.max(mapping.params.length, paramIndex + 1)}, (_, i) => mapping.params[i] ?? '');
    params[paramIndex] = value;
    this.countyMappingFields[key] = {...mapping, params};
  }

  transformTypeLabel(type: TransformType): string {
    return TRANSFORM_TYPE_OPTIONS.find((t) => t.value === type)?.label ?? type;
  }

  mappingSummary(mapping: CountyFieldMapping): string {
    if (mapping.params.length === 0 || mapping.params.every((p) => !p)) return '(not set)';
    return mapping.params.filter((p) => !!p).join(', ');
  }

  addCountyMappingSourceFile() {
    const name = this.countyMappingNewFileInput.trim();
    this.countyMappingNewFileError = '';
    if (!name) return;
    if (this.countyMappingSourceFiles.includes(name)) {
      this.countyMappingNewFileError = 'That file is already in the list.';
      return;
    }
    this.countyMappingSourceFiles = [...this.countyMappingSourceFiles, name];
    const parsed = parseHeaderRow(this.countyMappingNewFileHeaderInput);
    this.countyMappingSourceFileColumns[name] = parsed.columns;
    this.countyMappingSourceFileDelimiters[name] = parsed.delimiter;
    this.countyMappingNewFileInput = '';
    this.countyMappingNewFileHeaderInput = '';
  }

  /** Moves a source file up (-1) or down (+1). Order matters: the first file is the primary
   *  file the ingest walks, and unqualified columns resolve to the first file that has them. */
  moveCountyMappingSourceFile(name: string, delta: -1 | 1) {
    const files = [...this.countyMappingSourceFiles];
    const from = files.indexOf(name);
    const to = from + delta;
    if (from === -1 || to < 0 || to >= files.length) return;
    [files[from], files[to]] = [files[to], files[from]];
    this.countyMappingSourceFiles = files;
  }

  /** The column the parcelId mapping copies (without a file prefix), which the ingest keys
   *  one record per parcel off, or '' when parcelId isn't a copy yet. */
  get countyMappingParcelIdColumn(): string {
    const mapping = this.countyMappingFields['parcelId'];
    const source = mapping?.transformType === 'copy' ? mapping.params[0]?.trim() ?? '' : '';
    const primary = this.countyMappingSourceFiles[0];
    return primary && source.startsWith(`${primary}:`) ? source.slice(primary.length + 1) : source;
  }

  /** True when the primary (first) file has declared columns but not the one the parcelId
   *  mapping copies, so the ingest couldn't key one record per parcel off it. */
  get countyMappingPrimaryFileLacksParcelId(): boolean {
    const primary = this.countyMappingSourceFiles[0];
    const column = this.countyMappingParcelIdColumn.toLowerCase();
    if (!primary || !column) return false;
    const columns = (this.countyMappingSourceFileColumns[primary] ?? []).map(AdminComponent.columnNameOf);
    return columns.length > 0 && !columns.some((c) => c.trim().toLowerCase() === column);
  }

  removeCountyMappingSourceFile(name: string) {
    this.countyMappingSourceFiles = this.countyMappingSourceFiles.filter((f) => f !== name);
    delete this.countyMappingSourceFileColumns[name];
    delete this.countyMappingSourceFileDelimiters[name];
    delete this.countyMappingSourceFileUrls[name];
  }

  columnCountFor(file: string): number {
    return this.countyMappingSourceFileColumns[file]?.length ?? 0;
  }

  private static columnNameOf(column: string | FixedWidthColumn): string {
    return typeof column === 'string' ? column : column.name;
  }

  /** Every known column, both bare (`im_sq_ft`) and file-qualified
   *  (`building_res.txt:im_sq_ft`), for the param autocomplete. */
  get countyMappingKnownColumns(): string[] {
    const all = Object.entries(this.countyMappingSourceFileColumns).flatMap(([file, columns]) =>
      columns.map(AdminComponent.columnNameOf).flatMap((col) => [col, `${file}:${col}`]),
    );
    return [...new Set(all)].sort();
  }

  /** Whether a field's mapping references at least one column we actually know about, so a
   *  county with real declared files (like DCAD) can be worked through methodically instead
   *  of trusting every typed field name by eye. Constants are never flagged: they're
   *  deliberately not read from source data. Fields with no params yet are also not flagged,
   *  to avoid a wall of red on a totally blank mapping. */
  fieldNeedsAttention(mapping: CountyFieldMapping): boolean {
    if (mapping.transformType === 'constant') return false;
    const known = this.countyMappingKnownColumns;
    if (known.length === 0) return false;
    // Literal params (an operator, a separator, a list of codes) aren't columns, so they
    // neither count as a reference nor get flagged.
    const specs = TRANSFORM_TYPE_OPTIONS.find((t) => t.value === mapping.transformType)?.params ?? [];
    const filled = mapping.params.filter((p, i) => !!p && specs[i]?.kind !== 'literal');
    if (filled.length === 0) return false;
    // A param can itself be a comma-joined list (fallbackChain's "A, B, C"), so split every
    // param on commas before checking. Harmless for single-value params, which just split
    // into one piece.
    const pieces = filled.flatMap((p) => p.split(',').map((s) => s.trim()));
    return !pieces.some((p) => known.includes(p));
  }

  get countyMappingAttentionCount(): number {
    return KIN_FIELDS.filter((f) => this.fieldNeedsAttention(this.countyMappingFields[f.key])).length;
  }

  get countyMappingConfigJson(): string {
    return JSON.stringify(
      {
        sourceFiles: this.countyMappingSourceFiles,
        fields: this.countyMappingFields,
      },
      null,
      2,
    );
  }

}
